import { Inject, Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { from, lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { NavController } from '@ionic/angular';
import { LoadingService } from '@/services/loading-service/loading.service';
import { EnvironmentConfigService } from '@/core/config/environment-config.service';
import { AppEnvironmentAppEnvironmentService } from '@/api/proxy/app-environment/app-environment-services';
import { LoggerService } from '@/core/logging/logger.service';

@Injectable()
export class GlobalHttpInterceptor implements HttpInterceptor {
  private readonly logger = inject(LoggerService);

  constructor(
    private _loading: LoadingService,
    public navCtrl: NavController,
    public _EnvironmentConfigService: EnvironmentConfigService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.includes('/AppLogMessage')) {
      return next.handle(request);
    }

    if (request.url.includes(AppEnvironmentAppEnvironmentService.AppEnvironmentSettingsPostPath)) {
      return next.handle(request);
    }

    var skipLoader = request.headers.has('X-Skip-Loader') || request.headers.has('Skip-Loading') || request.headers.has('X-Silent-Request');
    var promise = this.handle(request, next, skipLoader);
    var req = from(promise).pipe(
      catchError((error) => {
        if (!skipLoader) {
          this._loading.setLoading(false, request.url);
        }

        if (error instanceof HttpErrorResponse) {
          if (error.error instanceof ErrorEvent) {
            this.logger.error('Error Event');
          } else {
            this.logger.log(`error status : ${error.status} ${error.statusText}`);
            switch (error.status) {
              //error message = error.error
              case 200: //Ok
                this.logger.log(error);
                break;
              case 400: //Bad Request
                break;
              case 401: //login
                break;
              case 403: //forbidden
                break;
              case 404: //not found
                break;
              case 500: //internal server error
                break;
              case 504: //gateway timeout
                break;
              case 501: //not implemented
                break;
              default: //general
                break;
            }
          }
        } else {
          this.logger.error('Other Errors');
        }
        if (!skipLoader) {
          this._loading.setLoading(false, request.url);
        }
        return throwError(() => error);
      }),
    );
    return req;
  }

  async handle(request: HttpRequest<unknown>, next: HttpHandler, skipLoader: boolean): Promise<HttpEvent<unknown>> {
    await this._EnvironmentConfigService.LoadConfigurationAsync();
    const deviceConfig = this._EnvironmentConfigService.DeviceConfig();
    const environmentConfig = this._EnvironmentConfigService.EnvironmentConfig();

    request = request.clone({
      headers: request.headers.set('user-language', this.document.documentElement.lang).set('ngrok-skip-browser-warning', 'true'),
    });

    if (deviceConfig != null && environmentConfig != null) {
      request = request.clone({
        headers: request.headers
          .set('ion-app-deviceType', deviceConfig.deviceType!)
          .set('ion-app-name', deviceConfig.name!)
          .set('ion-app-id', deviceConfig.id!)
          .set('ion-app-version', deviceConfig.version!)
          .set('ion-app-build', deviceConfig.build!)
          .set('ion-app-countryCode', environmentConfig.country!),
      });
    }

    var excludeToken = false;
    var config = this._EnvironmentConfigService.EnvironmentConfigOrEmpty();
    if (config) {
      if (request.url.includes(config.faceScanConfig!.faceScanBase!)) {
        excludeToken = true;
      }
    }

    if (!skipLoader) {
      await this._loading.setLoading(true, request.url);
    }

    const data = await lastValueFrom(
      next.handle(request).pipe(
        map<HttpEvent<unknown>, unknown>(async (evt: HttpEvent<unknown>) => {
          if (evt instanceof HttpResponse) {
            if (!skipLoader) {
              await this._loading.setLoading(false, request.url);
            }
          }
          return evt;
        }),
      ),
    );

    // The map above emits a promise that the async handle resolves, so data is the HttpEvent.
    return data as HttpEvent<unknown>;
  }
}
